import { formatVolume } from '../../../utils/format';
import {
    FLOW_GROUPS,
    hasFlow,
    type FlowGroup,
    type FlowGroupId,
    type LatestFlow,
    type LatestFlowByMarket,
} from '../../flow/groups';
import type { Outcome } from '../../model/types';
import { probabilityOf, volumeOf, type OutcomeSort } from '../ranking';

// What a phone row shows per measure. The desktop switch only sorts, and the
// desktop bar is always the probability; on a phone the switch also picks the
// row's figure and its bar (see the plan, "Decisions").

/** Two letters for the portrait that has no image: first and last word. */
export function initialsOf(label: string): string {
    const first = (word: string) =>
        Array.from(word.replace(/^[^\p{L}\p{N}]+/u, ''))[0] ?? '';
    const words = label
        .split(/\s+/)
        .map(first)
        .filter(letter => letter !== '');
    if (words.length === 0) return '?';
    const letters =
        words.length === 1 ? words[0] : words[0] + words[words.length - 1];
    return letters.toLocaleUpperCase();
}

/** "15.5%": one decimal, as the desktop row (LiveProbability) shows it. */
export function probabilityText(outcome: Outcome): string {
    const probability = probabilityOf(outcome);
    return probability === null ? '–' : `${(probability * 100).toFixed(1)}%`;
}

function shareOf(outcome: Outcome, totalVolume: number): number {
    return totalVolume > 0 ? volumeOf(outcome) / totalVolume : 0;
}

/** "19%": the outcome's share of the event's volume. */
export function shareText(outcome: Outcome, totalVolume: number): string {
    return `${(shareOf(outcome, totalVolume) * 100).toFixed(0)}%`;
}

/** "$48.5M (19%)": the desktop row's volume cell. */
export function volumeText(outcome: Outcome, totalVolume: number): string {
    return `${formatVolume(volumeOf(outcome))} (${shareText(outcome, totalVolume)})`;
}

export interface OutcomeFlow {
    group: FlowGroup;
    value: LatestFlow;
}

/**
 * The flow a Flow row leads with: Informed when the outcome has it, else
 * Whales (the order `flowRank` sorts by), or null.
 */
export function flowOf(
    flow: ReadonlyMap<FlowGroup['id'], LatestFlow> | undefined
): OutcomeFlow | null {
    for (const group of FLOW_GROUPS) {
        const value = flow?.get(group.id);
        if (value && hasFlow(value)) return { group, value };
    }
    return null;
}

/**
 * The biggest |net capital| of each group, among the markets whose leading
 * flow it is. A Flow row's bar is scaled within its own group: the list is
 * sorted Informed first, then Whales, so one scale for both would make the
 * sorted list look unsorted.
 */
export function largestFlowByGroup(
    marketIds: readonly string[],
    byMarket: LatestFlowByMarket
): ReadonlyMap<FlowGroupId, number> {
    const largest = new Map<FlowGroupId, number>();
    for (const id of marketIds) {
        const picked = flowOf(byMarket.get(id));
        if (!picked) continue;
        largest.set(
            picked.group.id,
            Math.max(
                largest.get(picked.group.id) ?? 0,
                Math.abs(picked.value.netCapital)
            )
        );
    }
    return largest;
}

export interface BarContext {
    totalVolume: number;
    /** The largest net capital of the row's own flow group. */
    largestFlow: number;
    flow?: OutcomeFlow | null;
}

/** How full the row's bar is, 0..1, for the measure the switch is on. */
export function barFraction(
    measure: OutcomeSort,
    outcome: Outcome,
    context: BarContext
): number {
    let fraction = 0;
    if (measure === 'probability') {
        fraction = probabilityOf(outcome) ?? 0;
    } else if (measure === 'volume') {
        fraction = shareOf(outcome, context.totalVolume);
    } else if (context.flow && context.largestFlow > 0) {
        fraction =
            Math.abs(context.flow.value.netCapital) / context.largestFlow;
    }
    return Math.min(1, Math.max(0, fraction));
}
